import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Download, Pencil } from 'lucide-react'
import { observer } from 'mobx-react'
import { useContext, useMemo, useState } from 'react'
import { useForm } from 'react-hook-form'
import { useParams } from 'react-router'

import { Button } from '@/components/elements/button'
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/elements/form'
import { Input } from '@/components/elements/input'
import { Skeleton } from '@/components/elements/skeleton'
import { Textarea } from '@/components/elements/textarea'
import { FileUploader } from '@/components/FileUploader'
import { getEntity, updateEntity } from '@/services/entities.service'
import { UserStoreContext } from '@/stores/UserStore'
import { toast } from '@/utils/helpers'

const DEFAULT_VALUES = {
  name: '',
  type: 'Business',
  logoPath: null,
  facilityCode: '',
  streetAddress: '',
  streetAddress2: '',
  city: '',
  state: '',
  zipCode: '',
  email: '',
  phoneNumber: '',
  welcomeMessage: '',
  welcomeVideoPath: null,
}

/**
 * Account Details
 *
 * This page allows Account Admins, Owners, and RescueX Admins to view and edit
 * the details of their account (entity).
 */
export const AccountDetails = observer(() => {
  const { entityId } = useParams()
  const { updateEntityInRoles } = useContext(UserStoreContext)
  const [isEditing, setIsEditing] = useState(false)

  // GET entity details
  const { data: entity, isPending: isLoadingEntity } = useQuery({
    queryKey: ['entity', entityId],
    queryFn: () => getEntity(entityId),
    enabled: !!entityId,
  })

  const defaultValues = useMemo(() => {
    if (!entity) return DEFAULT_VALUES

    const {
      id,
      name,
      type,
      logoPath,
      facilityCode,
      streetAddress,
      streetAddress2,
      city,
      state,
      zipCode,
      email,
      phoneNumber,
      welcomeMessage,
      welcomeVideoPath,
    } = entity

    return {
      ...DEFAULT_VALUES,
      id,
      name,
      type,
      logoPath: logoPath || null,
      facilityCode: facilityCode || '',
      streetAddress: streetAddress || '',
      streetAddress2: streetAddress2 || '',
      city: city || '',
      state: state || '',
      zipCode: zipCode || '',
      email: email || '',
      phoneNumber: phoneNumber || '',
      welcomeMessage: welcomeMessage || '',
      welcomeVideoPath: welcomeVideoPath || null,
    }
  }, [entity])

  const form = useForm({ defaultValues, values: defaultValues })

  // Watch file upload fields
  const watchLogo = form.watch('logoPath')
  const watchWelcomeVideo = form.watch('welcomeVideoPath')

  // Data Processing
  const handleError = (m) => toast(m, 'error')
  const handleSuccess = (m) => toast(m, 'success')
  const queryClient = useQueryClient()
  const { isPending, mutate: mutateEntity } = useMutation({
    mutationFn: (data) => updateEntity(data),
    onError: (err) => handleError(err.message),
    onSuccess: (data) => {
      handleSuccess(`${data.name} updated successfully`)
      setIsEditing(false)

      // Update entity in MobX store so sidebar logo refreshes immediately
      updateEntityInRoles(data)
      // Refresh entity data
      queryClient.invalidateQueries({ queryKey: ['entity', entityId] })
      // Refresh Entities lists for the EntitySelector
      queryClient.invalidateQueries({ queryKey: ['all-entities'] })
    },
  })

  /**
   * Handles form submission
   * @param {object} data
   */
  const onSubmit = async (data) => mutateEntity(data)

  /**
   * Shared render function for file uploads
   */
  const renderFileUpload = ({
    fieldName,
    label,
    acceptedFileTypes,
    filePrefix,
    signedPathKey,
    fileKey,
    watchValue,
    renderPreview,
  }) => {
    const signedPath = entity?.[signedPathKey]
    const originalFile = entity?.[fileKey]

    // Show preview if signed path exists and file is set
    if (signedPath && watchValue !== null) {
      return (
        <div className="flex flex-col gap-2">
          {renderPreview(signedPath)}
          {isEditing && (
            <Button
              type="button"
              variant="destructive"
              onClick={() => form.setValue(fieldName, null)}
              className="w-fit"
              disabled={isPending}
            >
              Remove {label}
            </Button>
          )}
        </div>
      )
    }

    // Show FileUploader if no file or file was removed
    if (isEditing && (!originalFile || watchValue !== originalFile)) {
      return (
        <FormField
          control={form.control}
          name={fieldName}
          render={() => (
            <FormItem>
              <FormLabel>{label}</FormLabel>
              <FormControl>
                <FileUploader
                  acceptedFileTypes={acceptedFileTypes}
                  handleUploadToServer={async (file) => {
                    form.setValue(fieldName, file.fileUploadName)
                  }}
                  filePrefix={filePrefix}
                  maxFiles={1}
                  type="aws"
                  disabled={isPending}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
      )
    }

    // Show placeholder with filename
    if (watchValue && isEditing) {
      return (
        <div className="flex flex-col gap-2">
          <div>
            <p className="text-foreground text-sm font-medium">{watchValue}</p>
            <small>Save the account to see the preview.</small>
          </div>
          <Button
            type="button"
            variant="destructive"
            onClick={() => form.setValue(fieldName, null)}
            className="w-fit"
            disabled={isPending}
          >
            Remove {label}
          </Button>
        </div>
      )
    }

    return null
  }

  const handleCancel = () => {
    form.reset(defaultValues)
    setIsEditing(false)
  }

  return isLoadingEntity ? (
    <div className="flex flex-col gap-6 py-4">
      {/* Form field placeholders (label + input pairs) */}
      {['sk-1', 'sk-2', 'sk-3', 'sk-4'].map((key) => (
        <div key={key} className="flex flex-col gap-2">
          <Skeleton className="h-4 w-32" />
          <Skeleton className="h-10 w-full" />
        </div>
      ))}
    </div>
  ) : (
    <>
      <div className="mb-4 flex w-full items-center justify-end">
        {!isEditing ? (
          <Button onClick={() => setIsEditing(true)} disabled={isLoadingEntity}>
            <Pencil className="mr-2 h-4 w-4" />
            Edit
          </Button>
        ) : (
          <div className="flex gap-2">
            <Button variant="outline" onClick={handleCancel} disabled={isPending}>
              Cancel
            </Button>
            <Button onClick={form.handleSubmit(onSubmit)} disabled={isPending}>
              Save Changes
            </Button>
          </div>
        )}
      </div>

      <Form {...form}>
        <form className="flex flex-col gap-6" onSubmit={form.handleSubmit(onSubmit)}>
          {/* Account Details Section */}
          <div className="flex flex-col gap-4 pt-4">
            {/* Logo Upload */}
            {renderFileUpload({
              fieldName: 'logoPath',
              label: 'Logo',
              acceptedFileTypes: ['image/*'],
              filePrefix: 'entity-logos',
              signedPathKey: 'logo',
              fileKey: 'logoPath',
              watchValue: watchLogo,
              renderPreview: (signedPath) => (
                <img
                  src={signedPath}
                  alt="Account Logo"
                  className="h-20 w-20 rounded object-contain"
                />
              ),
            })}

            {/* Name */}
            <FormField
              control={form.control}
              disabled={!isEditing || isPending}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Name</FormLabel>
                  <FormControl>
                    <Input {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
              rules={{ required: 'This field is required' }}
            />

            {/* Facility Code */}
            <FormField
              control={form.control}
              disabled={!isEditing || isPending}
              name="facilityCode"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Facility Code</FormLabel>
                  <FormControl>
                    <Input {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
              rules={{ required: 'This field is required' }}
            />
          </div>

          {/* Address Section */}
          <div className="flex flex-col gap-4">
            <span className="leading-6 font-bold">Address</span>

            {/* Street Address 1 */}
            <FormField
              control={form.control}
              disabled={!isEditing || isPending}
              name="streetAddress"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Street Address 1</FormLabel>
                  <FormControl>
                    <Input {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
              rules={{ required: 'This field is required' }}
            />

            {/* Street Address 2 */}
            <FormField
              control={form.control}
              disabled={!isEditing || isPending}
              name="streetAddress2"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Street Address 2</FormLabel>
                  <FormControl>
                    <Input {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* City */}
            <FormField
              control={form.control}
              disabled={!isEditing || isPending}
              name="city"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>City</FormLabel>
                  <FormControl>
                    <Input {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
              rules={{ required: 'This field is required' }}
            />

            {/* State and Zip Side by Side */}
            <div className="flex flex-row gap-4">
              <FormField
                control={form.control}
                disabled={!isEditing || isPending}
                name="state"
                render={({ field }) => (
                  <FormItem className="flex-1">
                    <FormLabel>State</FormLabel>
                    <FormControl>
                      <Input {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
                rules={{ required: 'This field is required' }}
              />

              <FormField
                control={form.control}
                disabled={!isEditing || isPending}
                name="zipCode"
                render={({ field }) => (
                  <FormItem className="flex-1">
                    <FormLabel>Zip</FormLabel>
                    <FormControl>
                      <Input {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
                rules={{ required: 'This field is required' }}
              />
            </div>
          </div>

          {/* Contact Details Section */}
          <div className="flex flex-col gap-4">
            <span className="leading-6 font-bold">Contact Details</span>

            {/* Email */}
            <FormField
              control={form.control}
              disabled={!isEditing || isPending}
              name="email"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Email</FormLabel>
                  <FormControl>
                    <Input type="email" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
              rules={{ required: 'This field is required' }}
            />

            {/* Phone */}
            <FormField
              control={form.control}
              disabled={!isEditing || isPending}
              name="phoneNumber"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Facility Phone</FormLabel>
                  <FormControl>
                    <Input type="tel" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
              rules={{ required: 'This field is required' }}
            />

            {/* Welcome Message */}
            <FormField
              control={form.control}
              disabled={!isEditing || isPending}
              name="welcomeMessage"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Welcome Message</FormLabel>
                  <FormControl>
                    <Textarea {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Welcome Video */}
            {renderFileUpload({
              fieldName: 'welcomeVideoPath',
              label: 'Welcome Video',
              acceptedFileTypes: ['video/mp4', 'video/webm'],
              filePrefix: 'entity-welcome-videos',
              signedPathKey: 'welcomeVideo',
              fileKey: 'welcomeVideoPath',
              watchValue: watchWelcomeVideo,
              renderPreview: (signedPath) => (
                <a
                  href={signedPath}
                  download
                  rel="noreferrer"
                  className="text-foreground hover:text-primary flex items-center gap-2 text-sm font-medium"
                >
                  <Download className="h-4 w-4" />
                  <span>Download Welcome Video</span>
                </a>
              ),
            })}
          </div>
        </form>
      </Form>
    </>
  )
})

AccountDetails.displayName = 'AccountDetails'
